/**
 * Dashboard entry point.
 *
 * Bootstraps the session manager (which creates the Auth0 SPA client up front so
 * the external-site login flow + callback handling are ready), the shared
 * `ApiClient` (token pulled live from the manager) and the TanStack Query
 * client, then mounts the provider stack + router. Auth0 callback restoration is
 * handled inside `<SessionProvider>` / `<RequireSession>` — no callback route.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { AppProviders } from "./app/providers";
import { createSessionManager } from "./app/session";
import { createApiClient } from "./app/api-client";
import { getRoutingToken, setRoutingToken } from "./app/routing-token";
import { configureCdnForApp } from "./app/cdn";
import { createDashboardStreamingClient } from "./app/streaming";
import { createDashboardGraphqlClient } from "./app/graphql";
import { createQueryClient } from "./config";
import { configureDisplayDefaults } from "./config/display";
import { appEnv } from "./config/env";
import { initRum } from "./app/observability";
import { router } from "./app/router";
import "./index.css";

async function bootstrap() {
  // App-wide explorer links (per chain) + hash/address truncation, applied to
  // the shared registries before anything renders.
  configureDisplayDefaults();

  // CloudWatch RUM (page loads, performance, JS errors, HTTP). No-op until the
  // RUM env ids are set. Separate from the ObservabilityProvider — see
  // ./app/observability for why.
  void initRum(appEnv);

  const manager = await createSessionManager();
  // When the gateway rejects a request because the token expired, force a fresh
  // token from Auth0 and push it into the synchronous routing-token snapshot the
  // router reads its bearer from (`__raw`), so the retried request carries it.
  // If the refresh fails (refresh token gone too), fall back to a re-login.
  const apiClient = createApiClient(
    () => manager.getAccessToken(),
    async () => {
      const fresh = await manager.refreshAccessToken();
      if (fresh) {
        const current = getRoutingToken();
        if (current) setRoutingToken({ ...current, __raw: fresh });
        return true;
      }
      await manager.login();
      return false;
    },
  );
  // Signed-cookie CDN delivery for cold transaction bodies (mints lazily).
  configureCdnForApp(apiClient);
  const streamingClient = createDashboardStreamingClient(() => manager.getAccessToken());
  // HTTP GraphQL read client (fast path); null when PRIVATE_API_GRAPHQL_URL is
  // unset, in which case every data operation stays on REST.
  const graphqlClient = createDashboardGraphqlClient(() => manager.getAccessToken());
  const queryClient = createQueryClient();

  const container = document.getElementById("root");
  if (!container) throw new Error("#root element not found");

  createRoot(container).render(
    <StrictMode>
      <AppProviders
        manager={manager}
        apiClient={apiClient}
        queryClient={queryClient}
        streamingClient={streamingClient}
        graphqlClient={graphqlClient}
      >
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
}

void bootstrap();
